import React, { useState } from 'react';
import { Play, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getUserQueueStatus } from '../../lib/queueManager';
import { useFamilyContext } from '../../contexts/FamilyContext';

/**
 * Admin control for manually triggering queue processing
 * Use this instead of waiting for cron jobs (Hobby plan = 2/day limit)
 */
const QueueProcessingControl = () => {
  const { currentUser } = useFamilyContext();
  const [processing, setProcessing] = useState(false);
  const [queueStatus, setQueueStatus] = useState(null);
  const [lastProcessed, setLastProcessed] = useState(null);

  // Fetch current queue status
  const fetchQueueStatus = async () => {
    if (!currentUser?.id) return;

    const result = await getUserQueueStatus(currentUser.id);
    if (result.success) {
      setQueueStatus(result.data);
    }
  };

  // Manually trigger queue processing
  const processQueue = async () => {
    setProcessing(true);

    try {
      const response = await fetch('/api/queue', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_CRON_SECRET || 'dev'}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'process' })
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message || 'Queue processing triggered!');
        setLastProcessed(new Date());

        // Refresh queue status after processing
        setTimeout(fetchQueueStatus, 2000);
      } else {
        toast.error(result.error || 'Failed to process queue');
      }
    } catch (error) {
      console.error('Error processing queue:', error);
      toast.error('Failed to trigger queue processing');
    } finally {
      setProcessing(false);
    }
  };

  // Load queue status on mount
  React.useEffect(() => {
    fetchQueueStatus();
  }, [currentUser]);

  if (!currentUser) return null;

  const activeJobs = queueStatus
    ? [...queueStatus.processing, ...queueStatus.queued, ...queueStatus.pending]
    : [];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Queue Processing
          </h3>
          <p className="text-sm text-gray-600">
            Manually trigger college data generation
          </p>
        </div>

        <button
          onClick={processQueue}
          disabled={processing || activeJobs.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Process Queue Now
            </>
          )}
        </button>
      </div>

      {/* Queue Status Summary */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-yellow-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-yellow-700 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-medium">Pending</span>
          </div>
          <p className="text-2xl font-bold text-yellow-900">
            {queueStatus?.pending.length || 0}
          </p>
        </div>

        <div className="bg-blue-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-blue-700 mb-1">
            <Loader2 className="w-4 h-4" />
            <span className="text-xs font-medium">Processing</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">
            {queueStatus?.processing.length || 0}
          </p>
        </div>

        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-green-700 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span className="text-xs font-medium">Completed</span>
          </div>
          <p className="text-2xl font-bold text-green-900">
            {queueStatus?.completed.length || 0}
          </p>
        </div>

        <div className="bg-red-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-red-700 mb-1">
            <XCircle className="w-4 h-4" />
            <span className="text-xs font-medium">Failed</span>
          </div>
          <p className="text-2xl font-bold text-red-900">
            {queueStatus?.failed.length || 0}
          </p>
        </div>
      </div>

      {/* Active Jobs List */}
      {activeJobs.length > 0 && (
        <div className="border-t border-gray-100 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Active Jobs</h4>
          <div className="space-y-2">
            {activeJobs.slice(0, 5).map(job => (
              <div
                key={job.id}
                className="flex items-center justify-between text-sm bg-gray-50 rounded-lg p-2"
              >
                <div className="flex items-center gap-2">
                  {job.status === 'processing' ? (
                    <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
                  ) : (
                    <Clock className="w-3 h-3 text-gray-400" />
                  )}
                  <span className="font-medium text-gray-900">
                    {job.college?.name || job.college?.global_colleges?.name}
                  </span>
                </div>
                <span className="text-xs text-gray-600 capitalize">
                  {job.job_type.replace('_', ' ')}
                </span>
              </div>
            ))}
            {activeJobs.length > 5 && (
              <p className="text-xs text-gray-500 text-center">
                +{activeJobs.length - 5} more jobs
              </p>
            )}
          </div>
        </div>
      )}

      {activeJobs.length === 0 && (
        <div className="text-center py-6 text-gray-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
          <p className="text-sm">No jobs in queue</p>
        </div>
      )}

      {lastProcessed && (
        <div className="mt-4 text-xs text-gray-500 text-center">
          Last processed: {lastProcessed.toLocaleTimeString()}
        </div>
      )}

      {/* Refresh Button */}
      <button
        onClick={fetchQueueStatus}
        className="w-full mt-4 text-xs text-blue-600 hover:text-blue-700"
      >
        Refresh Status
      </button>
    </div>
  );
};

export default QueueProcessingControl;
